/**
 * Helpers for finding `agentParameters('<name>')` references inside tool
 * action inputs, so the UI can warn before deleting a parameter still in use.
 */

import { isValueSegmentArray, segmentsToString } from '@/types/valueSegment';

// Captures the quoted argument of `agentParameters(...)`. Accepts backslash escapes
// (Otto `${...}` expressions, as emitted by useTokenSources) and doubled quotes
// (workflow-definition `@...` expressions).
const REFERENCE_SOURCE = String.raw`agentParameters\s*\(\s*(?:'((?:[^'\\]|\\.|'')*)'|"((?:[^"\\]|\\.|"")*)")\s*\)`;

const decodeLiteral = (raw: string, quote: string): string =>
  raw.replace(/\\(.)|''|""/gs, (match, escaped: string | undefined) => escaped ?? (match === quote + quote ? quote : match));

// Case-insensitive: the runtime resolves expression functions and agent
// parameter names case-insensitively (TemplateExpressionsHelper.OnGetAgentParameters).
const buildReferenceMatcher = (paramName: string): ((text: string) => boolean) => {
  const target = paramName.toLowerCase();
  return (text) => {
    // Fresh regex per call so global-flag lastIndex state never leaks between fields.
    for (const [, single, double] of text.matchAll(new RegExp(REFERENCE_SOURCE, 'gis'))) {
      const name = single !== undefined ? decodeLiteral(single, "'") : decodeLiteral(double ?? '', '"');
      if (name.toLowerCase() === target) return true;
    }
    return false;
  };
};

const countInValue = (value: unknown, matches: (text: string) => boolean, seen: WeakSet<object>): number => {
  if (typeof value === 'string') return matches(value) ? 1 : 0;
  // An editor field's ValueSegment[] is one field; token segments duplicate their expression in token.value.
  if (isValueSegmentArray(value)) return matches(segmentsToString(value)) ? 1 : 0;
  if (!value || typeof value !== 'object') return 0;
  if (seen.has(value)) return 0;
  seen.add(value);

  const children = Array.isArray(value) ? value : Object.values(value);
  return children.reduce<number>((total, child) => total + countInValue(child, matches, seen), 0);
};

/**
 * Count how many string fields reference the given agent parameter.
 * A field that references the same parameter several times counts once.
 */
export function countAgentParameterReferences(paramName: string, sources: readonly unknown[]): number {
  if (!paramName) return 0;
  const matches = buildReferenceMatcher(paramName);
  const seen = new WeakSet<object>();
  return sources.reduce<number>((total, source) => total + countInValue(source, matches, seen), 0);
}
